import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-deposit-fee-catalog',
  templateUrl: './deposit-fee-catalog.component.html',
  styleUrls: ['./deposit-fee-catalog.component.scss']
})
export class DepositFeeCatalogComponent implements OnInit {
  feeTypes: any[] = [];
  loading = false;
  saving = false;

  showForm = false;
  editingId: string | null = null;
  pushing = false;

  feeTypeForm!: FormGroup;

  constructor(
    private ecommerceService: EcommerceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadFeeTypes();
  }

  private static eanRangeValidator(group: AbstractControl): ValidationErrors | null {
    const start = group.get('eanRangeStart')?.value;
    const end = group.get('eanRangeEnd')?.value;
    if ((start && !end) || (!start && end)) {
      return { eanRangeIncomplete: true };
    }
    return null;
  }

  private initForm(): void {
    this.feeTypeForm = new FormGroup({
      name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      description: new FormControl(''),
      defaultAmount: new FormControl(0, [Validators.required, Validators.min(0)]),
      sortOrder: new FormControl(0, [Validators.min(0)]),
      active: new FormControl(true),
      eanRangeStart: new FormControl(''),
      eanRangeEnd: new FormControl(''),
      providerCode: new FormControl('')
    }, { validators: DepositFeeCatalogComponent.eanRangeValidator });
  }

  loadFeeTypes(): void {
    this.loading = true;
    this.spinner.show();
    this.ecommerceService.getDepositFeeTypes().subscribe(
      (res: any) => {
        this.loading = false;
        this.spinner.hide();
        const data = res?.response?.data ?? res?.data ?? res;
        this.feeTypes = Array.isArray(data) ? data : [];
      },
      (error: any) => {
        this.loading = false;
        this.spinner.hide();
        console.error('Failed to load deposit fee types', error);
        this.toastr.error('Failed to load deposit fee types');
      }
    );
  }

  openAddForm(): void {
    this.editingId = null;
    this.feeTypeForm.reset({ defaultAmount: 0, sortOrder: 0, active: true });
    this.showForm = true;
  }

  openEditForm(ft: any): void {
    this.editingId = ft._id;
    this.feeTypeForm.patchValue({
      name: ft.name,
      description: ft.description || '',
      defaultAmount: ft.defaultAmount,
      sortOrder: ft.sortOrder,
      active: ft.active,
      eanRangeStart: ft.eanRangeStart || '',
      eanRangeEnd: ft.eanRangeEnd || '',
      providerCode: ft.providerCode || ''
    });
    this.showForm = true;
  }

  cancelForm(): void {
    this.showForm = false;
    this.editingId = null;
    this.feeTypeForm.reset({ defaultAmount: 0, sortOrder: 0, active: true });
  }

  saveForm(): void {
    if (this.feeTypeForm.invalid) {
      this.feeTypeForm.markAllAsTouched();
      this.toastr.error('Please fill in all required fields');
      return;
    }

    this.saving = true;
    this.spinner.show();
    const payload = this.feeTypeForm.value;

    const obs = this.editingId
      ? this.ecommerceService.updateDepositFeeType(this.editingId, payload)
      : this.ecommerceService.createDepositFeeType(payload);

    obs.subscribe(
      (res: any) => {
        this.saving = false;
        this.spinner.hide();
        this.toastr.success(this.editingId ? 'Fee type updated' : 'Fee type created');
        this.cancelForm();
        this.loadFeeTypes();
      },
      (error: any) => {
        this.saving = false;
        this.spinner.hide();
        this.toastr.error(error.error?.response?.message || 'Failed to save fee type');
      }
    );
  }

  confirmDelete(ft: any): void {
    Swal.fire({
      title: 'Deactivate fee type?',
      html: `<strong>${ft.name}</strong> will be deactivated. Stores using this type will retain their settings, but it will no longer be assignable to new products.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, deactivate'
    }).then(result => {
      if (result.isConfirmed) {
        this.deleteFeeType(ft._id);
      }
    });
  }

  private deleteFeeType(id: string): void {
    this.spinner.show();
    this.ecommerceService.deleteDepositFeeType(id).subscribe(
      () => {
        this.spinner.hide();
        this.toastr.success('Fee type deactivated');
        this.loadFeeTypes();
      },
      (error: any) => {
        this.spinner.hide();
        this.toastr.error(error.error?.response?.message || 'Failed to deactivate fee type');
      }
    );
  }

  restoreFeeType(ft: any): void {
    this.spinner.show();
    this.ecommerceService.updateDepositFeeType(ft._id, { active: true }).subscribe(
      () => {
        this.spinner.hide();
        this.toastr.success('Fee type restored');
        this.loadFeeTypes();
      },
      (error: any) => {
        this.spinner.hide();
        this.toastr.error(error.error?.response?.message || 'Failed to restore fee type');
      }
    );
  }

  confirmAndPush(): void {
    if (confirm('This will sync global default amounts to all stores, resetting any per-store custom amounts. Continue?')) {
      this.pushing = true;
      this.pushToAllStores();
    }
  }

  pushToAllStores(): void {
    this.spinner.show();
    this.ecommerceService.pushDepositFeeTypesToAllStores().subscribe(
      (res: any) => {
        this.pushing = false;
        this.spinner.hide();
        const data = res?.response?.data ?? res?.data ?? res;
        const count = data?.storesUpdated ?? 0;
        this.toastr.success(`Global deposit fees pushed to ${count} stores`);
      },
      (error: any) => {
        this.pushing = false;
        this.spinner.hide();
        this.toastr.error('Failed to push deposit fees to stores');
      }
    );
  }

  eanRangeDisplay(ft: any): string {
    if (ft.eanRangeStart && ft.eanRangeEnd) {
      return `${ft.eanRangeStart} – ${ft.eanRangeEnd}`;
    }
    if (ft.eanRangeStart) return `${ft.eanRangeStart}+`;
    return '—';
  }
}
